import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ImageModel } from '../types';
import { listImageModels, createImageModel, updateImageModel, deleteImageModel } from '../api';
import Breadcrumb from '../components/Breadcrumb';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';
import { useToast } from '../components/Toast';

const emptyForm = (): Partial<ImageModel> => ({
  platform: '',
  model: '',
  displayName: '',
  pricePerUse: 0,
  creditsPerCall: 0,
  isActive: true,
});

const ImageModelConfig: React.FC = () => {
  const { t } = useTranslation('config');
  const toast = useToast();
  const [models, setModels] = useState<ImageModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<ImageModel | null>(null);
  const [form, setForm] = useState<Partial<ImageModel>>(emptyForm());
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<ImageModel | null>(null);

  const load = () => {
    setLoading(true);
    listImageModels().then(setModels).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm());
    setShowModal(true);
  };

  const openEdit = (m: ImageModel) => {
    setEditing(m);
    setForm({
      platform: m.platform,
      model: m.model,
      displayName: m.displayName,
      pricePerUse: m.pricePerUse,
      creditsPerCall: m.creditsPerCall,
      isActive: m.isActive,
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.platform || !form.model) {
      toast.error(t('imageModel.platformModelRequired'));
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await updateImageModel(editing.id, form);
        toast.success(t('imageModel.updateSuccess'));
      } else {
        await createImageModel(form);
        toast.success(t('imageModel.createSuccess'));
      }
      setShowModal(false);
      load();
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (m: ImageModel) => {
    try {
      await deleteImageModel(m.id);
      toast.success(t('imageModel.deleted'));
      load();
    } catch (e: any) {
      toast.error(e.message);
    }
    setDeleteConfirm(null);
  };

  // group by platform
  const platforms = Array.from(new Set(models.map(m => m.platform)));

  if (loading) {
    return (
      <div className="container">
        <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-3)' }}>{t('imageModel.loading')}</div>
      </div>
    );
  }

  return (
    <div className="container">
      <Breadcrumb items={[{ label: t('imageModel.breadcrumbHome'), to: '/projects' }, { label: t('imageModel.breadcrumbTitle') }]} />
      <div className="page-header">
        <h1 className="page-title">{t('imageModel.pageTitle')}</h1>
        <button className="btn btn-primary" onClick={openCreate}>{t('imageModel.addModel')}</button>
      </div>

      {platforms.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-3)' }}>
          {t('imageModel.noModels')}
        </div>
      ) : (
        platforms.map(platform => (
          <div key={platform} className="card" style={{ marginBottom: '1rem' }}>
            <h3 style={{ margin: '0 0 0.75rem 0', fontSize: '1rem' }}>{platform}</h3>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--border)' }}>
                  <th style={thStyle}>{t('imageModel.modelColumn')}</th>
                  <th style={thStyle}>{t('imageModel.displayNameColumn')}</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>{t('imageModel.pricePerUse')}</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>{t('imageModel.creditsPerCall')}</th>
                  <th style={{ ...thStyle, textAlign: 'center' }}>{t('imageModel.statusColumn')}</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>{t('imageModel.actionsColumn')}</th>
                </tr>
              </thead>
              <tbody>
                {models.filter(m => m.platform === platform).map(m => (
                  <tr key={m.id} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={tdStyle}><code style={{ fontSize: '0.8rem' }}>{m.model}</code></td>
                    <td style={tdStyle}>{m.displayName || '-'}</td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>¥{m.pricePerUse.toFixed(4)}</td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>{m.creditsPerCall || '-'}</td>
                    <td style={{ ...tdStyle, textAlign: 'center' }}>
                      <span className={`status-badge ${m.isActive ? 'status-completed' : 'status-failed'}`} style={{ fontSize: '0.75rem' }}>
                        {m.isActive ? t('imageModel.enabled') : t('imageModel.stopped')}
                      </span>
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>
                      <button className="btn btn-ghost" style={{ fontSize: '0.75rem', padding: '0.2rem 0.5rem', marginRight: '0.25rem' }} onClick={() => openEdit(m)}>{t('imageModel.edit')}</button>
                      <button className="btn btn-ghost" style={{ fontSize: '0.75rem', padding: '0.2rem 0.5rem', color: 'var(--error)' }} onClick={() => setDeleteConfirm(m)}>{t('imageModel.delete')}</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))
      )}

      <Modal open={showModal} title={editing ? t('imageModel.editModel') : t('imageModel.addModelTitle')} onClose={() => setShowModal(false)} width="420px">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          <label style={labelStyle}>
            {t('imageModel.platformLabel')}
            {editing ? (
              <input className="input" value={form.platform || ''} disabled style={{ opacity: 0.6, cursor: 'not-allowed' }} />
            ) : (
              <select className="input" value={form.platform || ''} onChange={e => setForm(f => ({ ...f, platform: e.target.value }))}>
                <option value="">{t('imageModel.selectPlatform')}</option>
                {platforms.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            )}
          </label>
          <label style={labelStyle}>
            {t('imageModel.modelNameLabel')}
            <input className="input" value={form.model || ''} onChange={e => setForm(f => ({ ...f, model: e.target.value }))} placeholder={t('imageModel.modelNamePlaceholder')} disabled={!!editing} style={editing ? { opacity: 0.6, cursor: 'not-allowed' } : undefined} />
          </label>
          <label style={labelStyle}>
            {t('imageModel.displayNameLabel')}
            <input className="input" value={form.displayName || ''} onChange={e => setForm(f => ({ ...f, displayName: e.target.value }))} placeholder={t('imageModel.displayNamePlaceholder')} />
          </label>
          <label style={labelStyle}>
            {t('imageModel.pricePerUseLabel')}
            <input className="input" type="number" step="0.0001" value={form.pricePerUse ?? 0} onChange={e => setForm(f => ({ ...f, pricePerUse: parseFloat(e.target.value) || 0 }))} />
          </label>
          <label style={{ ...labelStyle, color: 'var(--warning)' }}>
            {t('imageModel.creditsPerCallLabel')}
            <input className="input" type="number" step="1" min="0" value={form.creditsPerCall ?? 0} onChange={e => setForm(f => ({ ...f, creditsPerCall: parseInt(e.target.value) || 0 }))} />
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem' }}>
            <input type="checkbox" checked={form.isActive ?? true} onChange={e => setForm(f => ({ ...f, isActive: e.target.checked }))} />
            {t('imageModel.enableCheckbox')}
          </label>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '0.5rem' }}>
            <button className="btn btn-ghost" onClick={() => setShowModal(false)}>{t('imageModel.cancel')}</button>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? t('imageModel.saving') : t('imageModel.save')}</button>
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        open={!!deleteConfirm}
        title={t('imageModel.confirmDeleteTitle')}
        message={t('imageModel.confirmDeleteMessage', { name: deleteConfirm?.displayName || deleteConfirm?.model || '' })}
        onConfirm={() => deleteConfirm && handleDelete(deleteConfirm)}
        onCancel={() => setDeleteConfirm(null)}
      />
    </div>
  );
};

const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '0.6rem 0.75rem',
  fontWeight: 600,
  color: 'var(--text-2)',
  whiteSpace: 'nowrap',
};

const tdStyle: React.CSSProperties = {
  padding: '0.6rem 0.75rem',
  whiteSpace: 'nowrap',
};

const labelStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.25rem',
  fontSize: '0.85rem',
  color: 'var(--text-2)',
};

export default ImageModelConfig;
